import type { SyncProvider } from './syncProvider.js';
import {
  REMOTE_ROOT_PREFIX, REMOTE_MODULES_PREFIX, REMOTE_STORAGE_PREFIX, REMOTE_CHECKPOINTS_PREFIX,
} from './types.js';

/**
 * Drive "sweep" — finds stale files left on the sync provider that the
 * reconciler no longer needs, so the user can reclaim space and tidy up.
 *
 * Two categories, by how safe they are to remove:
 *   - SAFE: tombstones older than the retention window, and storage snapshots
 *     whose module is a tombstone (the module is deleted — its data is orphaned).
 *   - REVIEW: storage snapshots with NO module file at all. Ambiguous: it could
 *     be a module that syncs DATA only (code stays local), so removing it would
 *     destroy live data. Surfaced separately, never swept by default.
 *
 * Checkpoints (shell/checkpoints/) are never touched — they have their own
 * retention.
 */

/** Tombstones younger than this may still be propagating to offline devices. */
export const TOMBSTONE_RETENTION_DAYS = 30;

export interface StaleFile {
  path: string;
  kind: 'tombstone' | 'orphan-data' | 'review-data';
  reason: string;
  sizeBytes: number;
}

export interface SweepScan {
  safe: StaleFile[];     // pre-selected for removal
  review: StaleFile[];   // shown, opt-in only
  scanned: number;
}

const DAY_MS = 86_400_000;

function idFromPath(path: string, prefix: string): string {
  return path.slice(`${prefix}/`.length).replace(/\.json$/, '');
}

/** Inspects the provider and classifies stale files. Reads every code snapshot. */
export async function scanStaleFiles(provider: SyncProvider): Promise<SweepScan> {
  const all = await provider.listFiles(REMOTE_ROOT_PREFIX);
  const files = all.filter((f) => !f.path.startsWith(`${REMOTE_CHECKPOINTS_PREFIX}/`));
  const codeFiles = files.filter((f) => f.path.startsWith(`${REMOTE_MODULES_PREFIX}/`) && f.path.endsWith('.json'));
  const dataFiles = files.filter((f) => f.path.startsWith(`${REMOTE_STORAGE_PREFIX}/`) && f.path.endsWith('.json'));

  // Read code snapshots to learn which module ids are live vs tombstoned.
  const liveIds = new Set<string>();
  const tombstones = new Map<string, number>(); // id → deletedAtMs
  await Promise.all(codeFiles.map(async (f) => {
    const id = idFromPath(f.path, REMOTE_MODULES_PREFIX);
    try {
      const json = await provider.readFile(f.path);
      const o = json ? (JSON.parse(json) as { deleted?: boolean; deletedAtMs?: number; lastModifiedMs?: number }) : null;
      if (o && o.deleted === true) tombstones.set(id, o.deletedAtMs || o.lastModifiedMs || 0);
      else liveIds.add(id); // unreadable or live → treat as live (never sweep its data)
    } catch {
      liveIds.add(id);
    }
  }));

  const now = Date.now();
  const safe: StaleFile[] = [];
  const review: StaleFile[] = [];

  // Stale tombstones — older than the retention window.
  for (const f of codeFiles) {
    const id = idFromPath(f.path, REMOTE_MODULES_PREFIX);
    if (!tombstones.has(id)) continue;
    const deletedAt = tombstones.get(id)!;
    const ageDays = deletedAt ? Math.floor((now - deletedAt) / DAY_MS) : null;
    if (ageDays !== null && ageDays >= TOMBSTONE_RETENTION_DAYS) {
      safe.push({ path: f.path, kind: 'tombstone', reason: `deleted ${ageDays}d ago`, sizeBytes: f.sizeBytes });
    }
  }

  // Storage snapshots: orphaned (paired with a tombstone) vs ambiguous (no module).
  for (const f of dataFiles) {
    const id = idFromPath(f.path, REMOTE_STORAGE_PREFIX);
    if (liveIds.has(id)) continue; // live module → keep its data
    if (tombstones.has(id)) {
      safe.push({ path: f.path, kind: 'orphan-data', reason: 'module deleted — data orphaned', sizeBytes: f.sizeBytes });
    } else {
      review.push({ path: f.path, kind: 'review-data', reason: 'no module on Drive — may sync data only', sizeBytes: f.sizeBytes });
    }
  }

  return { safe, review, scanned: files.length };
}

/** Deletes the given paths. Best-effort; reports counts. */
export async function sweepFiles(provider: SyncProvider, paths: string[]): Promise<{ deleted: number; failed: number }> {
  let deleted = 0, failed = 0;
  for (const p of paths) {
    try { if (await provider.deleteFile(p)) deleted++; else failed++; }
    catch { failed++; }
  }
  return { deleted, failed };
}
